/**
 * Admin File Management Page
 *
 * (for viva) Security Design:
 * - This page shows FILE METADATA ONLY — no encrypted content, no IV, no salt,
 *   no hash, no storage path. The admin CANNOT decrypt or download files.
 * - Admin can schedule files for deletion with a grace period, giving owners
 *   time to backup their data before permanent removal.
 * - Admin can cancel scheduled deletions or force-delete after grace period.
 * - All actions are audit-logged on the backend for accountability.
 * - This is consistent with the CDEaaS model: admin is the service provider
 *   who manages infrastructure, NOT a data owner with decryption capability.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  getAdminFiles,
  getAdminFilesCount,
  scheduleFileDelete,
  cancelFileDelete,
  forceDeleteFile,
} from '../services/api';
import { formatRelative as tzFormatRelative } from '../utils/time';

function AdminFilesPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('uploaded_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const PAGE_SIZE = 15;

  // Schedule delete modal state (admin: no OTP required)
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [graceDays, setGraceDays] = useState(7);
  const [actionLoading, setActionLoading] = useState(false);

  // Force delete modal state (admin: no OTP required)
  const [forceModal, setForceModal] = useState(null);

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);
      const [data, countData] = await Promise.all([
        getAdminFiles(search, sortBy, order, PAGE_SIZE, page * PAGE_SIZE),
        getAdminFilesCount(search),
      ]);
      setFiles(data || []);
      setTotal(countData?.total || 0);
    } catch (e) {
      setError(e.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, order, page]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setOrder('desc');
    }
    setPage(0);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const openScheduleDeleteModal = (file) => {
    setDeleteModal(file);
    setDeleteReason('');
    setGraceDays(7);
  };

  const handleScheduleDelete = async () => {
    if (!deleteModal || !deleteReason.trim()) return;
    setActionLoading(true);
    setError('');
    try {
      await scheduleFileDelete(deleteModal.id, deleteReason, graceDays);
      setSuccess(`File '${deleteModal.original_filename}' scheduled for deletion in ${graceDays} days`);
      setDeleteModal(null);
      setDeleteReason('');
      setGraceDays(7);
      loadFiles();
    } catch (e) {
      setError(e.message || 'Failed to schedule deletion');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelDelete = async (file) => {
    if (!window.confirm(`Cancel scheduled deletion of '${file.original_filename}'?`)) return;
    setError('');
    try {
      await cancelFileDelete(file.id);
      setSuccess(`Deletion cancelled for '${file.original_filename}'`);
      loadFiles();
    } catch (e) {
      setError(e.message || 'Failed to cancel deletion');
    }
  };

  const openForceDeleteModal = (file) => {
    setForceModal(file);
  };

  const handleForceDeleteConfirm = async () => {
    if (!forceModal) return;
    setActionLoading(true);
    setError('');
    try {
      await forceDeleteFile(forceModal.id);
      setSuccess(`File '${forceModal.original_filename}' permanently deleted`);
      setForceModal(null);
      loadFiles();
    } catch (e) {
      setError(e.message || 'Failed to delete file');
    } finally {
      setActionLoading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isGraceExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) <= new Date();
  };

  const getTimeRemaining = (dateStr) => {
    if (!dateStr) return '';
    const diff = new Date(dateStr) - new Date();
    if (diff <= 0) return 'Expired';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  const securityLevelBadge = (level) => {
    const colors = {
      standard: { bg: 'rgba(99,102,241,0.2)', color: '#C4B5FD', label: 'Standard' },
      enhanced: { bg: 'rgba(34,197,94,0.2)', color: 'var(--green)', label: 'Enhanced' },
      maximum: { bg: 'rgba(239,68,68,0.2)', color: 'var(--red)', label: 'Maximum' },
    };
    const c = colors[level] || colors.enhanced;
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
        fontSize: 11, fontWeight: 600, backgroundColor: c.bg, color: c.color,
      }}>
        {c.label}
      </span>
    );
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const sortArrow = (col) => {
    if (sortBy !== col) return '';
    return order === 'asc' ? ' \u2191' : ' \u2193';
  };

  return (
    <div className="page-container admin-page-shell admin-tactile-page">
      {/* Header */}
      <div style={s.headerRow}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', marginBottom: 8 }}>File Governance</div>
          <h2 style={s.pageTitle}>File Management</h2>
          <p style={s.subtitle}>
            Review file metadata and manage scheduled deletions. Admin cannot decrypt files.
          </p>
        </div>
        <button onClick={loadFiles} style={s.refreshBtn} className="hover-action-btn">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div style={s.errorMsg}>
          {error}
          <button onClick={() => setError('')} style={s.dismissBtn}>&times;</button>
        </div>
      )}
      {success && (
        <div style={s.successMsg}>
          {success}
          <button onClick={() => setSuccess('')} style={s.dismissBtn}>&times;</button>
        </div>
      )}

      {/* Search Bar */}
      <div style={s.searchRow}>
        <div style={s.searchBox}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Search by filename..."
            value={search}
            onChange={handleSearch}
            style={s.searchInput}
          />
        </div>
        <span style={s.totalCount}>{total} file{total !== 1 ? 's' : ''} total</span>
      </div>

      {/* Table */}
      <div className="table-responsive" style={s.tableWrap}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            Loading files...
          </div>
        ) : files.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            {search ? 'No files match your search.' : 'No encrypted files in the system.'}
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th} onClick={() => handleSort('original_filename')}>
                  Filename{sortArrow('original_filename')}
                </th>
                <th style={{ ...s.th, width: 80 }} onClick={() => handleSort('file_size')}>
                  Size{sortArrow('file_size')}
                </th>
                <th style={s.th}>Owner</th>
                <th style={{ ...s.th, width: 100 }}>Algorithm</th>
                <th style={{ ...s.th, width: 90 }} onClick={() => handleSort('security_level')}>
                  Level{sortArrow('security_level')}
                </th>
                <th style={{ ...s.th, width: 120 }} onClick={() => handleSort('uploaded_at')}>
                  Uploaded{sortArrow('uploaded_at')}
                </th>
                <th style={{ ...s.th, width: 140 }}>Status</th>
                <th style={{ ...s.th, width: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map(f => {
                const isScheduled = !!f.scheduled_delete_at;
                const graceExpired = isGraceExpired(f.scheduled_delete_at);
                return (
                  <tr key={f.id} style={isScheduled ? s.scheduledRow : {}}>
                    <td style={s.td}>
                      <div style={s.filename}>{f.original_filename}</div>
                    </td>
                    <td style={{ ...s.td, fontSize: 12 }}>{formatSize(f.file_size)}</td>
                    <td style={s.td}>
                      <div style={{ fontSize: 12 }}>{f.owner_name || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{f.owner_email}</div>
                    </td>
                    <td style={{ ...s.td, fontSize: 11, fontFamily: 'monospace' }}>{f.encryption_algorithm}</td>
                    <td style={s.td}>{securityLevelBadge(f.security_level)}</td>
                    <td style={{ ...s.td, fontSize: 12 }}>
                      {f.uploaded_at ? tzFormatRelative(f.uploaded_at) : '—'}
                    </td>
                    <td style={s.td}>
                      {isScheduled ? (
                        <div>
                          <span style={s.deleteBadge}>
                            Scheduled for deletion
                          </span>
                          <div style={{ fontSize: 10, color: graceExpired ? 'var(--red)' : 'var(--yellow)', marginTop: 2 }}>
                            {getTimeRemaining(f.scheduled_delete_at)}
                          </div>
                          {f.delete_reason && (
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }} title={f.delete_reason}>
                              Reason: {f.delete_reason.length > 30 ? f.delete_reason.slice(0, 30) + '...' : f.delete_reason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={s.activeBadge}>Active</span>
                      )}
                    </td>
                    <td style={s.td}>
                      {isScheduled ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button onClick={() => handleCancelDelete(f)} style={s.cancelBtn} className="hover-action-btn">
                            Cancel
                          </button>
                          {graceExpired && (
                            <button onClick={() => openForceDeleteModal(f)} style={s.forceDeleteBtn} className="hover-action-btn">
                              Delete Now
                            </button>
                          )}
                        </div>
                      ) : (
                        <button onClick={() => openScheduleDeleteModal(f)} style={s.scheduleBtn} className="schedule-delete-outline-btn hover-action-btn">
                          Schedule Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={s.pagination}>
          <button
            disabled={page === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            style={s.pageBtn}
            className="hover-action-btn"
          >
            &laquo; Prev
          </button>
          <span style={s.pageInfo}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
            style={s.pageBtn}
            className="hover-action-btn"
          >
            Next &raquo;
          </button>
        </div>
      )}

      {/* Security Note */}
      <div style={s.securityNote}>
        <div style={s.securityNoteHeader}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <strong>Admin File Management Policy</strong>
        </div>
        <span>
          As an admin (service provider), you can view file metadata and schedule deletions with a grace period.
          You CANNOT download, decrypt, or access file contents. All actions are audit-logged.
          File owners are notified via email and in-app notification when their files are scheduled for deletion.
        </span>
      </div>

      {/* Schedule Delete Modal — Admin: no OTP, direct form */}
      {deleteModal && (
        <>
          <div style={s.overlay} onClick={() => setDeleteModal(null)} />
          <div style={s.modal}>
            <h3 style={s.modalTitle}>Schedule File Deletion</h3>
            <p style={s.modalSubtitle}>
              File: <strong>{deleteModal.original_filename}</strong>
              <br />
              Owner: {deleteModal.owner_email}
            </p>
            <label style={s.label}>Reason for deletion *</label>
            <textarea
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
              placeholder="Enter the reason for scheduling deletion..."
              style={s.textarea}
              rows={3}
            />
            <label style={s.label}>Grace period (days)</label>
            <input
              type="number"
              value={graceDays}
              onChange={e => setGraceDays(Math.max(1, parseInt(e.target.value) || 1))}
              min={1}
              max={90}
              style={s.input}
            />
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 16px' }}>
              The file owner will be notified and has {graceDays} day{graceDays !== 1 ? 's' : ''} to download a backup.
            </p>
            <div style={s.modalActions}>
              <button onClick={() => setDeleteModal(null)} style={s.modalCancelBtn}>Cancel</button>
              <button
                onClick={handleScheduleDelete}
                disabled={!deleteReason.trim() || actionLoading}
                style={{ ...s.modalConfirmBtn, opacity: !deleteReason.trim() || actionLoading ? 0.5 : 1 }}
              >
                {actionLoading ? 'Scheduling...' : 'Schedule Deletion'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Force Delete Modal — Admin: no OTP, confirm only */}
      {forceModal && (
        <>
          <div style={s.overlay} onClick={() => setForceModal(null)} />
          <div style={s.modal}>
            <h3 style={{ ...s.modalTitle, color: 'var(--red)' }}>Permanently Delete File</h3>
            <p style={s.modalSubtitle}>
              File: <strong>{forceModal.original_filename}</strong>
              <br />
              Owner: {forceModal.owner_email}
            </p>
            <p style={{ fontSize: 12, color: 'var(--red)', marginBottom: 16, padding: '10px 12px', backgroundColor: 'rgba(239,68,68,0.15)', borderRadius: 6 }}>
              This will permanently delete the file. The owner has been notified. This cannot be undone.
            </p>
            <div style={s.modalActions}>
              <button onClick={() => setForceModal(null)} style={s.modalCancelBtn}>Cancel</button>
              <button onClick={handleForceDeleteConfirm} disabled={actionLoading} style={{ ...s.modalConfirmBtn, backgroundColor: '#991B1B' }}>
                {actionLoading ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────
const s = {
  headerRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 32, gap: 16, flexWrap: 'wrap',
  },
  pageTitle: {
    fontSize: 22, fontWeight: 900, margin: '0 0 6px 0', letterSpacing: '-0.4px', lineHeight: 1.1,
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6, fontWeight: 500 },
  refreshBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '10px 18px', borderRadius: 14,
    border: '1.5px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-85)',
    cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14,
    fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(99,102,241,0.06)',
  },

  errorMsg: {
    backgroundColor: 'rgba(239,68,68,0.08)', border: '1.5px solid rgba(239,68,68,0.15)', color: 'var(--red)',
    padding: '14px 18px', borderRadius: 16, marginBottom: 20,
    fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  successMsg: {
    backgroundColor: 'rgba(34,197,94,0.08)', border: '1.5px solid rgba(34,197,94,0.15)', color: 'var(--green)',
    padding: '14px 18px', borderRadius: 16, marginBottom: 20,
    fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  dismissBtn: {
    background: 'none', border: 'none', fontSize: 18, cursor: 'pointer',
    color: 'inherit', padding: '0 4px', lineHeight: 1,
  },

  searchRow: {
    display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap',
  },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px',
    backgroundColor: 'var(--surface-90)', border: '1.5px solid rgba(99,102,241,0.1)',
    borderRadius: 16, flex: 1, maxWidth: 420,
    boxShadow: '0 2px 8px rgba(99,102,241,0.04)', transition: 'all 0.2s ease',
  },
  searchInput: {
    border: 'none', outline: 'none', fontSize: 14, flex: 1,
    backgroundColor: 'transparent', color: 'var(--text-primary)',
    fontFamily: 'inherit',
  },
  totalCount: { fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 },

  tableWrap: {
    backgroundColor: 'var(--surface-85)', borderRadius: 22,
    border: '1.5px solid rgba(99,102,241,0.08)', overflow: 'auto', marginBottom: 24,
    boxShadow: '0 4px 24px rgba(99,102,241,0.06)',
    backdropFilter: 'blur(12px)',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '14px 18px', textAlign: 'left', fontSize: 11, fontWeight: 800,
    color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px',
    borderBottom: '2px solid rgba(99,102,241,0.08)', cursor: 'pointer',
    userSelect: 'none', whiteSpace: 'nowrap',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.05), rgba(139,92,246,0.03))',
  },
  td: {
    padding: '14px 18px', borderBottom: '1px solid rgba(99,102,241,0.05)',
    verticalAlign: 'middle', lineHeight: 1.5, fontSize: 13, color: 'var(--text-primary)',
  },
  scheduledRow: {
    backgroundColor: 'rgba(239,68,68,0.02)',
  },
  filename: {
    fontSize: 13, fontWeight: 700, color: 'var(--text-primary)',
    maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },

  deleteBadge: {
    display: 'inline-block', padding: '4px 10px', borderRadius: 10,
    fontSize: 11, fontWeight: 700, backgroundColor: 'rgba(239,68,68,0.1)',
    color: 'var(--red)', border: '1px solid rgba(239,68,68,0.12)',
  },
  activeBadge: {
    display: 'inline-block', padding: '4px 10px', borderRadius: 10,
    fontSize: 11, fontWeight: 700, backgroundColor: 'rgba(34,197,94,0.1)',
    color: 'var(--green)', border: '1px solid rgba(34,197,94,0.12)',
  },

  scheduleBtn: {
    padding: '8px 16px', borderRadius: 14,
    border: '1.5px solid rgba(239,68,68,0.2)', backgroundColor: 'transparent',
    color: 'var(--red)', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.3s ease',
    minWidth: 110, textAlign: 'center',
  },
  cancelBtn: {
    padding: '8px 16px', borderRadius: 14,
    border: '1.5px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-85)',
    color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit', minWidth: 70, textAlign: 'center',
    transition: 'all 0.2s ease',
  },
  forceDeleteBtn: {
    padding: '8px 16px', borderRadius: 14,
    border: 'none', background: 'linear-gradient(135deg, #EF4444, #DC2626)',
    color: '#fff', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit', minWidth: 90, textAlign: 'center',
    boxShadow: '0 3px 10px rgba(239,68,68,0.25)', transition: 'all 0.3s ease',
  },

  pagination: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 16, marginBottom: 32,
  },
  pageBtn: {
    padding: '10px 18px', borderRadius: 14,
    border: '1.5px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-85)',
    color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.25s ease',
  },
  pageInfo: { fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 },

  securityNote: {
    background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.03))',
    padding: '16px 20px', borderRadius: 18, fontSize: 13, color: 'var(--text-muted)',
    lineHeight: 1.6, border: '1.5px solid rgba(99,102,241,0.1)',
  },
  securityNoteHeader: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)',
  },

  // Modal
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15,16,40,0.5)', zIndex: 1000,
    backdropFilter: 'blur(6px)',
  },
  modal: {
    position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    backgroundColor: 'var(--surface-95)', borderRadius: 24,
    padding: 32, width: 460, maxWidth: '90vw', zIndex: 1001,
    boxShadow: '0 24px 80px rgba(99,102,241,0.18), 0 8px 24px rgba(0,0,0,0.08)',
    border: '1.5px solid rgba(99,102,241,0.1)',
    backdropFilter: 'blur(20px)',
  },
  modalTitle: { fontSize: 17, fontWeight: 800, margin: '0 0 10px', color: 'var(--text-primary)', letterSpacing: '-0.3px' },
  modalSubtitle: { fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 22px', lineHeight: 1.6 },
  label: { fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.8px' },
  textarea: {
    width: '100%', padding: '12px 14px', border: '1.5px solid rgba(99,102,241,0.1)',
    borderRadius: 14, fontSize: 14, fontFamily: 'inherit',
    resize: 'vertical', marginBottom: 18, boxSizing: 'border-box',
    color: 'var(--text-primary)', backgroundColor: 'var(--surface-90)',
    transition: 'all 0.2s ease',
  },
  input: {
    width: 80, padding: '10px 14px', border: '1.5px solid rgba(99,102,241,0.1)',
    borderRadius: 14, fontSize: 14, fontFamily: 'inherit',
    color: 'var(--text-primary)', backgroundColor: 'var(--surface-90)',
  },
  modalActions: {
    display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 10,
  },
  modalCancelBtn: {
    padding: '10px 22px', borderRadius: 14,
    border: '1.5px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-85)',
    color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s ease',
  },
  modalConfirmBtn: {
    padding: '10px 22px', borderRadius: 14,
    border: 'none', background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
    color: '#fff', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 3px 12px rgba(99,102,241,0.3)', transition: 'all 0.3s ease',
  },
};

export default AdminFilesPage;
